import { AfterViewChecked, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CdkDragDrop, transferArrayItem, moveItemInArray } from '@angular/cdk/drag-drop';
import { ApiService } from 'src/app/api.service';
import { ThemingService } from 'src/app/theming.service';
import { LambdaService } from 'src/app/lambda.service';
import {
  buildReviewSettingsPayload,
  createDefaultReviewSettings,
  isReviewComponentType,
  normalizeReviewSettingsFromApi,
} from '../shared/component-form.helpers';
declare let $: any;

type ComponentListType = 'inactive' | 'active';

@Component({
  selector: 'app-components',
  templateUrl: './components.component.html',
  styleUrls: ['./components.component.scss']
})
export class ComponentsComponent implements OnInit, AfterViewChecked {
  @ViewChild('inactiveListElement') inactiveListElement?: ElementRef<HTMLElement>;
  @ViewChild('activeListElement') activeListElement?: ElementRef<HTMLElement>;
  @Output() editComponentEvent = new EventEmitter<number>();
  @Input() pageId: string | null = null;
  @Output() close = new EventEmitter<void>();
  modalDelete: boolean = false;
  inactive: any[] = [];
  active: any[] = [];
  showSuccessAlert: boolean = false;
  showErrorAlert: boolean = false;
  loading: boolean = false;
  component: any;
  componentToDeleteId: number | null = null;
  isCreatingComponent = false;
  isListingComponent = true;
  isEditingComponent = false;
  editComponentId!: number;
  pages: any[] = [];
  pageName: string | null = null;
  deletedInfoIds: string[] = [];
  confirmClone: boolean = false;
  selectedItem: any = null;
  selectedPageId: number | null = null;
  private scrollSnapshot = {
    inactive: 0,
    active: 0
  };
  private shouldRestoreScroll = false;

  constructor(
    private apiService: ApiService,
    private themingService: ThemingService,
    private lambdaService: LambdaService
  ) { }

  ngOnInit() {
    this.getComponents();
    if (this.pageId) {
    }
    this.getPages()
  }

  ngAfterViewChecked(): void {
    if (this.shouldRestoreScroll) {
      this.restoreScrollPositions();
    }
  }

  async getComponents() {
    this.captureScrollPositions();
    this.loading = true;
    try {
      const response: any = await this.lambdaService.getComponents(this.themingService.shopId);
      const componentRows = Array.isArray(response.data) ? response.data : [];
      this.component = componentRows.map((component: any) => ({
        ...component,
        componentInfo: typeof component.componentInfo === 'string'
        ? JSON.parse(component.componentInfo)
        : component.componentInfo
      }));
      const filteredComponents = this.pageId
      ? this.component.filter((component: any) => component.pageId === this.pageId)
      : this.component;
      this.inactive = this.sortComponentsByPosition(filteredComponents.filter((component: any) => component.status === 0));
      this.active = this.sortComponentsByPosition(filteredComponents.filter((component: any) => component.status === 1));

      this.loading = false;
      this.queueScrollRestore();
    } catch (error) {
      console.log(error);
      this.loading = false;
      this.queueScrollRestore();
    }
  }

  async getPages() {
    this.loading = true;
    try {
      const response: any = await this.lambdaService.getPages(this.themingService.shopId);
      const pageRows = Array.isArray(response.data) ? response.data : [];
      this.pages = pageRows;
      if (this.pageId) {
        const numericPageId = Number(this.pageId); // Convertir a número antes de comparar
        const matchedPage = this.pages.find(page => page.pageId === numericPageId);
        this.pageName = matchedPage ? matchedPage.pageName : 'Página no encontrada';
      }
      this.loading = false;
    } catch (error) {
      console.log(error);
      this.loading = false;
    }
  }

  editComponent(componentId: number): void {
    this.captureScrollPositions();
    this.isEditingComponent = true;
    this.isCreatingComponent = false;
    this.isListingComponent = false;
    this.editComponentId = componentId;
  }

  toggleComponentVisibility(creating: boolean) {
    this.captureScrollPositions();
    this.isCreatingComponent = creating;
    this.isListingComponent = !creating;
    if (!creating) {
      this.getComponents();
    }
  }

  cancelCreation() {
    this.captureScrollPositions();
    this.isCreatingComponent = false;
    this.isListingComponent = true;
    this.getComponents();
  }

  cancelEdition(): void {
    this.captureScrollPositions();
    this.isEditingComponent = false;
    this.isListingComponent = true;
    this.getComponents();
  }

  drop(event: CdkDragDrop<any[]>, listType: ComponentListType) {
    if (event.previousContainer === event.container && event.previousIndex === event.currentIndex) {
      return;
    }

    this.captureScrollPositions();
    if (event.previousContainer !== event.container) {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    } else {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }

    this.normalizeComponentLists();
    this.updateStatusAndPositions();
  }

  updatePositions() {
    this.normalizeComponentLists();
  }

  normalizeComponentLists() {
    this.inactive.forEach((component, index) => {
      component.status = 0;
      component.position = index + 1;
    });
    this.active.forEach((component, index) => {
      component.status = 1;
      component.position = index + 1;
    });
  }

  updateStatusAndPositions() {
    this.normalizeComponentLists();
    const allComponents = [...this.inactive, ...this.active];
    const componentDataString = allComponents.map(component =>
      `${component.componentId},${component.position},${component.status}`
    ).join(';');

    this.lambdaService.updateComponentStatusAndPosition(
      componentDataString,
      this.themingService.shopId
    )
    .then((response: any) => {
      if (response.statusCode === 200) {
        this.showSuccessAlert = true;
      } else {
        this.showErrorAlert = true;
      }
      setTimeout(() => {
        this.showSuccessAlert = false;
        this.showErrorAlert = false;
      }, 1000);
    })
    .catch((error) => {
      console.error(error);
      this.showErrorAlert = true;
      setTimeout(() => {
          this.showErrorAlert = false;
        }, 1000);
      });
    }

  confirmDelete(componentId: number) {
    this.captureScrollPositions();
    this.componentToDeleteId = componentId;
    this.modalDelete = true;
  }

  async deleteComponent(response: boolean) {
    if (response && this.componentToDeleteId !== null) {
      this.captureScrollPositions();
      this.loading = true;
      try {
        const component: any = this.component.find((comp: any) => comp.componentId === this.componentToDeleteId);
        if (!component) {
          return;
        }
        const urlsToDelete: string[] = [];
        if (component.componentInfo && Array.isArray(component.componentInfo)) {
          component.componentInfo.forEach((info: any) => {
            if (info.infoImageUrl) urlsToDelete.push(info.infoImageUrl);
            if (info.infoVideoUrl) urlsToDelete.push(info.infoVideoUrl);
          });
        }
        await this.deletePreviousFiles(urlsToDelete);
        await this.lambdaService.deleteComponent(this.themingService.shopId, this.componentToDeleteId);
        await this.getComponents();
        $('#deleteComponentModal').modal('show');
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
        this.modalDelete = false;
      }
    } else {
      this.modalDelete = false;
    }
  }

  async deletePreviousFiles(urlsToDelete: string[]): Promise<void> {
    if (urlsToDelete.length === 0) {
      return;
    }
    const deleteS3Promises = [];
    for (const url of urlsToDelete) {
      if (url) {
        const cleanedUrl = url.replace('https://mobydyg-files.s3.amazonaws.com/', '');
        deleteS3Promises.push(this.lambdaService.deleteFile(cleanedUrl));
      }
    }
    try {
      await Promise.all(deleteS3Promises);
    } catch (error) {
      console.error(error);
    }
  }

  toggleCloneDropdown(item: any) {
    if (!item.cloneEnabled) {
      item.selectedPageId = null;
    }
  }

  async cloneComponent(originalComponent: any, newPageId: number, cloneMedia: boolean) {
    if (!newPageId) return;

    try {
      this.captureScrollPositions();
      this.loading = true;
      const clonedComponent = { ...originalComponent };
      const translatedContent = this.resolveOptionalTranslatedContent(clonedComponent.translatedContent);
      clonedComponent.componentInfo = Array.isArray(clonedComponent.componentInfo) ? clonedComponent.componentInfo : [];
      let createResponse: any;

      delete clonedComponent.componentId;
      clonedComponent.pageId = newPageId;
      clonedComponent.status = 0;

      if (!cloneMedia) {
        clonedComponent.componentInfo.forEach((info: any) => {
          info.infoImageUrl = '';
          info.infoVideoUrl = '';
        });
      }

      if (translatedContent) {
        createResponse = await this.lambdaService.createComponent(
          0,
          this.themingService.shopId,
          this.sanitizeString(clonedComponent.componentName),
          this.sanitizeString(clonedComponent.title),
          this.sanitizeString(clonedComponent.subtitle),
          clonedComponent.position,
          clonedComponent.status,
          this.sanitizeString(clonedComponent.description),
          this.sanitizeString(clonedComponent.shopContactMail),
          clonedComponent.componentTypeId,
          clonedComponent.itemQuantity,
          clonedComponent.itemListId,
          clonedComponent.itemSort,
          clonedComponent.componentInfo.map((info: { infoImageUrl: any; }) => this.sanitizeString(info.infoImageUrl || '')).join('|'),
          clonedComponent.componentInfo.map((info: { infoDescription: any; }) => this.sanitizeString(info.infoDescription || '')).join('|'),
          clonedComponent.componentInfo.map((info: { redirectUrl: any; }) => this.sanitizeString(info.redirectUrl || '')).join('|'),
          clonedComponent.componentInfo.map((info: { infoTitle: any; }) => this.sanitizeString(info.infoTitle || '')).join('|'),
          clonedComponent.componentInfo.map((info: { urlName: any; }) => this.sanitizeString(info.urlName || '')).join('|'),
          clonedComponent.collectionId,
          clonedComponent.categoryId,
          clonedComponent.subCategoryId,
          clonedComponent.subSubCategoryId,
          clonedComponent.productComponentType || 0,
          clonedComponent.componentInfo.map((info: { infoVideoUrl: any; }) => info.infoVideoUrl || '').join('|'),
          clonedComponent.componentInfo.map((info: { videoUrlType: any; }) => info.videoUrlType || 0).join('|'),
          clonedComponent.pageId,
          "",
          translatedContent,
          clonedComponent.componentConfig
        );
      } else {
        createResponse = await this.lambdaService.createComponent(
          0,
          this.themingService.shopId,
          this.sanitizeString(clonedComponent.componentName),
          this.sanitizeString(clonedComponent.title),
          this.sanitizeString(clonedComponent.subtitle),
          clonedComponent.position,
          clonedComponent.status,
          this.sanitizeString(clonedComponent.description),
          this.sanitizeString(clonedComponent.shopContactMail),
          clonedComponent.componentTypeId,
          clonedComponent.itemQuantity,
          clonedComponent.itemListId,
          clonedComponent.itemSort,
          clonedComponent.componentInfo.map((info: { infoImageUrl: any; }) => this.sanitizeString(info.infoImageUrl || '')).join('|'),
          clonedComponent.componentInfo.map((info: { infoDescription: any; }) => this.sanitizeString(info.infoDescription || '')).join('|'),
          clonedComponent.componentInfo.map((info: { redirectUrl: any; }) => this.sanitizeString(info.redirectUrl || '')).join('|'),
          clonedComponent.componentInfo.map((info: { infoTitle: any; }) => this.sanitizeString(info.infoTitle || '')).join('|'),
          clonedComponent.componentInfo.map((info: { urlName: any; }) => this.sanitizeString(info.urlName || '')).join('|'),
          clonedComponent.collectionId,
          clonedComponent.categoryId,
          clonedComponent.subCategoryId,
          clonedComponent.subSubCategoryId,
          clonedComponent.productComponentType || 0,
          clonedComponent.componentInfo.map((info: { infoVideoUrl: any; }) => info.infoVideoUrl || '').join('|'),
          clonedComponent.componentInfo.map((info: { videoUrlType: any; }) => info.videoUrlType || 0).join('|'),
          clonedComponent.pageId,
          "",
          undefined,
          clonedComponent.componentConfig
        );
      }

      if (isReviewComponentType(Number(originalComponent.componentTypeId || 0), [], originalComponent.componentTypeName || '')) {
        const newComponentId = this.resolveCreatedComponentId(createResponse);
        if (!newComponentId) {
          throw new Error('The reviews component was cloned but the API did not return componentId.');
        }

        await this.cloneReviewSettings(Number(originalComponent.componentId || 0), newComponentId);
      }

      this.loading = false;
      $('#clonedComponentModal').modal('show');
      await this.getComponents();
    } catch (error) {
      console.error("Error al clonar el componente:", error);
      this.loading = false;
      $('#errorClonedComponentModal').modal('show');
    }
  }

  async moveComponent(component: any, newPageId: number) {
    if (!newPageId) return;

    try {
      this.captureScrollPositions();
      this.loading = true;
      const translatedContent = this.resolveOptionalTranslatedContent(component.translatedContent);
      component.pageId = newPageId;
      component.status = 0;

      if (translatedContent) {
        await this.lambdaService.updateComponent(
          component.componentId,
          this.themingService.shopId,
          this.sanitizeString(component.title),
          this.sanitizeString(component.subtitle),
          this.sanitizeString(component.description),
          this.sanitizeString(component.shopContactMail),
          component.itemQuantity,
          component.itemListId,
          component.itemSort,
          component.componentInfo.map((info: { infoImageUrl: any; }) => this.sanitizeString(info.infoImageUrl || '')).join('|'),
          component.componentInfo.map((info: { infoDescription: any; }) => this.sanitizeString(info.infoDescription || '')).join('|'),
          component.componentInfo.map((info: { redirectUrl: any; }) => this.sanitizeString(info.redirectUrl || '')).join('|'),
          component.componentInfo.map((info: { infoId: { toString: () => any; }; }) => info.infoId?.toString() || '').join('|'),
          this.deletedInfoIds.join('|'),
          component.componentInfo.map((info: { infoTitle: any; }) => this.sanitizeString(info.infoTitle || '')).join('|'),
          component.componentInfo.map((info: { urlName: any; }) => this.sanitizeString(info.urlName || '')).join('|'),
          component.collectionId,
          component.categoryId,
          component.subCategoryId,
          component.subSubCategoryId,
          component.productComponentType || 0,
          component.componentTypeId,
          this.sanitizeString(component.componentName),
          component.infoVideoUrl,
          component.videoUrlType,
          component.pageId,
          component.status,
          "",
          translatedContent,
          component.componentConfig
        );
      } else {
        await this.lambdaService.updateComponent(
          component.componentId,
          this.themingService.shopId,
          this.sanitizeString(component.title),
          this.sanitizeString(component.subtitle),
          this.sanitizeString(component.description),
          this.sanitizeString(component.shopContactMail),
          component.itemQuantity,
          component.itemListId,
          component.itemSort,
          component.componentInfo.map((info: { infoImageUrl: any; }) => this.sanitizeString(info.infoImageUrl || '')).join('|'),
          component.componentInfo.map((info: { infoDescription: any; }) => this.sanitizeString(info.infoDescription || '')).join('|'),
          component.componentInfo.map((info: { redirectUrl: any; }) => this.sanitizeString(info.redirectUrl || '')).join('|'),
          component.componentInfo.map((info: { infoId: { toString: () => any; }; }) => info.infoId?.toString() || '').join('|'),
          this.deletedInfoIds.join('|'),
          component.componentInfo.map((info: { infoTitle: any; }) => this.sanitizeString(info.infoTitle || '')).join('|'),
          component.componentInfo.map((info: { urlName: any; }) => this.sanitizeString(info.urlName || '')).join('|'),
          component.collectionId,
          component.categoryId,
          component.subCategoryId,
          component.subSubCategoryId,
          component.productComponentType || 0,
          component.componentTypeId,
          this.sanitizeString(component.componentName),
          component.infoVideoUrl,
          component.videoUrlType,
          component.pageId,
          component.status,
          "",
          undefined,
          component.componentConfig
        );
      }

      $('#movedComponentModal').modal('show');
      await this.getComponents();
      this.loading = false;
    } catch (error) {
      console.error("Error al mover el componente:", error);
      this.loading = false;
      $('#errorMovedComponentModal').modal('show');
    }
  }


  sanitizeString(str: string): string {
    if (!str) return '';
    return str
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r');
  }

  private resolveOptionalTranslatedContent(value: unknown): string | undefined {
    if (typeof value === 'string') {
      return value.trim() ? value : undefined;
    }

    if (value && typeof value === 'object') {
      const serialized = JSON.stringify(value);
      return serialized && serialized !== '{}' ? serialized : undefined;
    }

    return undefined;
  }

  private resolveCreatedComponentId(response: any): number {
    const candidates = [
      response?.componentId,
      response?.data?.componentId,
      response?.data?.[0]?.componentId,
      response?.component?.componentId,
      response?.createdComponent?.componentId,
      response?.insertId,
      response?.id,
    ];

    const matchedId = candidates.map((value) => Number(value || 0)).find((value) => value > 0);
    return matchedId || 0;
  }

  private async cloneReviewSettings(originalComponentId: number, newComponentId: number): Promise<void> {
    let sourceSettings: any;

    try {
      sourceSettings = await this.lambdaService.getReviewComponentSettings(originalComponentId, this.themingService.shopId);
    } catch (error) {
      console.error('Error fetching source review settings for clone:', error);
      sourceSettings = createDefaultReviewSettings(this.themingService.shopId, originalComponentId);
    }

    const payload = buildReviewSettingsPayload({
      ...normalizeReviewSettingsFromApi(sourceSettings, this.themingService.shopId, originalComponentId),
      componentId: newComponentId,
      shopId: this.themingService.shopId,
    });

    await this.lambdaService.upsertReviewComponentSettings(newComponentId, this.themingService.shopId, payload);
  }

  toggleAction(item: any, action: string) {
    if (item.actionType === action) {
      item.actionType = null;
    } else {
      item.actionType = action;
    }
  }



  onPageSelectionChange(event: Event, item: any) {
    const selectElement = event.target as HTMLSelectElement;
    if (selectElement && selectElement.value) {
      const selectedPageId = parseInt(selectElement.value, 10);

      if (item.actionType === 'clone') {
        this.selectedItem = item;
        this.selectedPageId = selectedPageId;
        this.confirmClone = true;
      } else if (item.actionType === 'move') {
        this.moveComponent(item, selectedPageId);
      }
    }
  }

  handleCloneResponse(userConfirmed: boolean, item: any, newPageId: number) {
    this.confirmClone = false;
    if (userConfirmed) {
      this.cloneComponent(item, newPageId, true);
    } else {
      this.cloneComponent(item, newPageId, false);
    }
  }

  closeModal() {
    $('#deleteComponentModal').modal('hide');
    $('#clonedComponentModal').modal('hide');
    $('#errorClonedComponentModal').modal('hide');
    $('#movedComponentModal').modal('hide');
    $('#errorMovedComponentModal').modal('hide');
  }

  onClose() {
    this.close.emit();
  }

  private sortComponentsByPosition(components: any[]): any[] {
    return components
      .map((component, index) => ({ component, index }))
      .sort((left, right) => {
        const leftPosition = Number(left.component.position || 0);
        const rightPosition = Number(right.component.position || 0);

        if (leftPosition > 0 && rightPosition > 0 && leftPosition !== rightPosition) {
          return leftPosition - rightPosition;
        }

        if (leftPosition > 0 && rightPosition <= 0) {
          return -1;
        }

        if (leftPosition <= 0 && rightPosition > 0) {
          return 1;
        }

        return left.index - right.index;
      })
      .map(({ component }) => component);
  }

  private captureScrollPositions(): void {
    if (this.inactiveListElement?.nativeElement) {
      this.scrollSnapshot.inactive = this.inactiveListElement.nativeElement.scrollTop;
    }

    if (this.activeListElement?.nativeElement) {
      this.scrollSnapshot.active = this.activeListElement.nativeElement.scrollTop;
    }
  }

  private queueScrollRestore(): void {
    this.shouldRestoreScroll = true;
    setTimeout(() => this.restoreScrollPositions());
  }

  private restoreScrollPositions(): void {
    const inactiveElement = this.inactiveListElement?.nativeElement;
    const activeElement = this.activeListElement?.nativeElement;

    if (!inactiveElement && !activeElement) {
      return;
    }

    if (inactiveElement) {
      inactiveElement.scrollTop = this.scrollSnapshot.inactive;
    }

    if (activeElement) {
      activeElement.scrollTop = this.scrollSnapshot.active;
    }

    this.shouldRestoreScroll = false;
  }

  }
